// ProductsPrintPage.js

import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../services/api';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';

function ProductsPrintPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const searchParam = params.get('search') || '';
  const storageParam = params.get('storage_id') || '';
  const orderingParam = params.get('ordering') || '';
  const inventoryMinParam = params.get('inventory_min') || '';

  // Grab userProfile to read dynamic config
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};
  const uiConfig = userProfile?.ui_config || {};

  // Product fields configuration
  const productFields = uiConfig.product_fields || [];
  const productFieldsSchema = uiConfig.product_fields_schema || {};

  // Check if storage/inventory is enabled
  const allowOrderManagement = uiConfig.allow_order_management || false;
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled && allowOrderManagement;

  // Fetch all products for printing
  const {
    data: products = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['products', 'print', searchParam, storageParam, orderingParam, inventoryMinParam],
    queryFn: async () => {
      const queryParams = { search: searchParam };
      if (storageParam) {
        queryParams.storage_id = storageParam;
      }
      if (orderingParam) {
        queryParams.ordering = orderingParam;
      }
      if (inventoryMinParam) {
        queryParams.inventory_min = inventoryMinParam;
      }
      const response = await API.get('/products/print/', { params: queryParams });
      return response.data;
    },
  });

  // On load, open new window & print
  useEffect(() => {
    if (!isLoading && !isError && products.length > 0) {
      const timer = setTimeout(() => {
        printProductsInNewWindow(products);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isLoading, isError, products]);

  // ==================== PRINT LOGIC ====================
  const printProductsInNewWindow = (productsData) => {
    const htmlContent = generatePrintHTML(productsData);

    openPrintWindow(htmlContent);
    navigate('/products');
  };

  // Helper: get the label from the schema
  const getFieldLabel = (fieldKey) => {
    const schema = productFieldsSchema[fieldKey];
    return schema?.label || fieldKey;
  };

  // Helper: check if field is included
  const isFieldIncluded = (fieldName) => {
    return productFields.includes(fieldName);
  };

  // Helper: get product field value
  const getProductValue = (product, fieldKey) => {
    // Check for extra_fields
    if (product.extra_fields && product.extra_fields[fieldKey] !== undefined) {
      return product.extra_fields[fieldKey] ?? '';
    }
    // Regular product field
    return product[fieldKey] ?? '';
  };

  // Helper: count total columns (for layout decisions)
  const countColumns = (productsData) => {
    let count = 2; // row number + ProductName (always)
    if (isFieldIncluded('CatalogNumber')) count++;
    if (isFieldIncluded('Barcode')) count++;
    if (isFieldIncluded('unit')) count++;
    count++; // Price
    if (storageEnabled) {
      const storageSet = new Set();
      productsData.forEach(p => (p.inventory || []).forEach(inv => storageSet.add(inv.StorageID)));
      count += storageSet.size + 1; // per-storage + total
    }
    return count;
  };

  // ============== Build raw HTML with dynamic columns ==============
  const generatePrintHTML = (productsData) => {
    // Count columns first to determine layout
    const colCount = countColumns(productsData);
    const useLandscape = colCount > 8;
    const fontSize = colCount > 12 ? 10 : colCount > 8 ? 12 : 14;

    const style = `
      <style>
        @media print {
          @page {
            margin: 8mm;
            ${useLandscape ? 'size: landscape;' : ''}
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
          }
        }
        table {
          border-collapse: collapse;
          width: 100%;
          font-family: Arial, sans-serif;
          direction: rtl;
          table-layout: fixed;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 3px 2px;
          text-align: center;
          font-size: ${fontSize}px;
          overflow: hidden;
          text-overflow: ellipsis;
          word-wrap: break-word;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        td:first-child, th:first-child {
          width: 30px;
        }
      </style>
    `;

    // Build filter description
    const filterParts = [];
    if (searchParam) {
      filterParts.push(`חיפוש: ${searchParam}`);
    }
    if (inventoryMinParam) {
      const formattedNum = parseFloat(inventoryMinParam).toLocaleString('he-IL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      filterParts.push(`מלאי >= ${formattedNum}`);
    }
    const filterSentence = filterParts.length > 0
      ? `<p style="font-size: 14px; font-family: Arial, sans-serif; direction: rtl;">סינון: ${filterParts.join(' | ')}</p>`
      : '';

    // Build columns configuration - whitelist approach
    const columns = [];

    // 1. ProductName - always shown
    columns.push({ key: 'ProductName', label: 'שם מוצר' });

    // 2. CatalogNumber - if in product_fields
    if (isFieldIncluded('CatalogNumber')) {
      columns.push({ key: 'CatalogNumber', label: getFieldLabel('CatalogNumber') || 'מק"ט' });
    }

    // 3. Barcode - if in product_fields
    if (isFieldIncluded('Barcode')) {
      columns.push({ key: 'Barcode', label: getFieldLabel('Barcode') || 'ברקוד' });
    }

    // 4. Unit - if in product_fields
    if (isFieldIncluded('unit')) {
      columns.push({ key: 'unit', label: getFieldLabel('unit') || 'יחידה' });
    }

    // 5. Price - always shown
    columns.push({ key: 'Price', label: productFieldsSchema.Price?.label || 'מחיר' });

    // 6. Inventory columns - if storage is enabled
    if (storageEnabled) {
      // Per-storage columns first (breakdown before total)
      const storageMap = new Map();
      productsData.forEach(p => {
        (p.inventory || []).forEach(inv => {
          if (!storageMap.has(inv.StorageID)) {
            storageMap.set(inv.StorageID, inv.StorageName);
          }
        });
      });
      storageMap.forEach((name, id) => {
        columns.push({ key: `storage_${id}`, label: name, storageId: id });
      });

      // Total inventory column last
      columns.push({ key: 'total_quantity', label: 'סה"כ מלאי' });
    }

    // Build the table header
    const headersHTML = columns
      .map((col) => `<th>${col.label}</th>`)
      .join('');

    const tableHeader = `
      <thead>
        <tr>
          <th>מס'</th>
          ${headersHTML}
        </tr>
      </thead>
    `;

    // Build the rows
    const currencyFormat = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' });

    const rowsHTML = productsData
      .map((product, index) => {
        const cellsHTML = columns
          .map((col) => {
            let val;
            if (col.key === 'total_quantity') {
              val = product.total_quantity ?? 0;
            } else if (col.key === 'Price') {
              val = currencyFormat.format(product.Price ?? 0);
            } else if (col.storageId) {
              const inv = (product.inventory || []).find(i => i.StorageID === col.storageId);
              val = inv ? inv.available_quantity : 0;
            } else {
              val = getProductValue(product, col.key);
            }
            return `<td>${val}</td>`;
          })
          .join('');

        return `
          <tr>
            <td>${index + 1}</td>
            ${cellsHTML}
          </tr>
        `;
      })
      .join('');

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        ${style}
        <title>רשימת מוצרים</title>
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        ${filterSentence}
        <table>
          ${tableHeader}
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  // ============== RENDER STATES ==============
  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          טוען נתונים להדפסה...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, color: 'red' }}>
        <Typography variant="h6">שגיאה בטעינת המוצרים להדפסה</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/products')}>
          חזרה לעמוד הקודם
        </Button>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">לא נמצאו מוצרים להדפסה</Typography>
        <Button variant="outlined" onClick={() => navigate('/products')} sx={{ mt: 2 }}>
          חזרה לעמוד הקודם
        </Button>
      </Box>
    );
  }

  // If loaded successfully, we show a minimal screen
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6">פתיחת חלון חדש להדפסה...</Typography>
      <Button variant="outlined" onClick={() => navigate('/products')} sx={{ mt: 2 }}>
        חזרה לעמוד הקודם
      </Button>
    </Box>
  );
}

export default ProductsPrintPage;
